import React, { useEffect, useMemo, useState } from 'react';
import { TYPE_COLORS } from './BrainMap';
import './BrainHero.css';

// BrainHero — the brain as geometry. The first zoom level above the map:
// a nucleus (name, page count, the three liveness verdicts as arcs), a left
// hemisphere of curated architecture lobes with one live number each, and a
// right hemisphere that is the wiki — one node per page type, fanning out to
// the real hub pages (most inbound links) as satellites. Every node is a
// door into a denser view. Identical in the plane and the kit; each page
// composes the lobes from data it already loads.
//
// props:
//   name, sub          nucleus title and one-line caption
//   pages              total wiki pages (the nucleus number)
//   verdicts           { populated, updating, current } → { ok, level, reason }
//   onOpenCenter       click on the nucleus (→ the map)
//   lobes              [{ id, label, value, sub, tone: 'ok'|'warn'|'err', live, onOpen }]
//   wiki               { types: [{ type, count, onOpen }], hubs: [{ key, title, type, backlinks, stale, thin, errors, onOpen }] }
//   heat               { key|slug|docId → served count } (from the context heat)
//   note               extra sentence for the data-nova-note

const W = 1000;
const H = 600;
const CX = 500;
const CY = 300;
const TYPE_ORDER = ['system', 'decision', 'lesson', 'runbook', 'concept'];
const TYPE_LABEL = { system: 'Systems', decision: 'Decisions', lesson: 'Lessons', runbook: 'Runbooks', concept: 'Concepts' };
const LEVEL_CLASS = { ok: 'is-ok', warn: 'is-warn', down: 'is-down' };

const pt = (deg, rx, ry) => { const a = (deg * Math.PI) / 180; return { x: CX + rx * Math.cos(a), y: CY - ry * Math.sin(a) }; };
const at = (p) => ({ left: `${(p.x / W) * 100}%`, top: `${(p.y / H) * 100}%` });
const fmt = (v) => (v == null || v === '' ? '—' : typeof v === 'number' ? v.toLocaleString() : String(v));

// A dendrite: leaves the nucleus sideways and arrives sideways, so the
// bundle reads as fibres rather than spokes.
function dendrite(a, b) {
  const dx = (b.x - a.x) * 0.55;
  return `M${a.x},${a.y} C${a.x + dx},${a.y} ${b.x - dx},${b.y} ${b.x},${b.y}`;
}
// A 300° arc around the nucleus, open at the bottom.
function arcPath(r) {
  const s = pt(240, r, r);
  const e = pt(300, r, r);
  return `M${s.x},${s.y} A${r},${r} 0 1 1 ${e.x},${e.y}`;
}

function useNarrow(bp = 700) {
  const [narrow, setNarrow] = useState(() => (typeof window !== 'undefined' ? window.innerWidth < bp : false));
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${bp - 1}px)`);
    const on = () => setNarrow(mq.matches);
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [bp]);
  return narrow;
}

function layout(lobes, types, hubs) {
  // Left hemisphere: lobes staggered on two ellipses between 100° and 260°.
  const n = lobes.length;
  const lobePts = lobes.map((l, i) => {
    const deg = n === 1 ? 180 : 100 + (i * 160) / (n - 1);
    const outer = n > 5 && i % 2 === 1;
    return { ...l, p: pt(deg, outer ? 405 : 295, outer ? 258 : 190) };
  });
  // Right hemisphere: one node per type between 60° and -60°.
  const m = types.length;
  const typePts = types.map((t, i) => {
    const deg = m === 1 ? 0 : 60 - (i * 120) / (m - 1);
    return { ...t, deg, p: pt(deg, 330, 196) };
  });
  // Satellites: each type's hubs on the outer ellipse around the type's angle.
  const hubPts = [];
  for (const t of typePts) {
    const mine = hubs.filter((h) => h.type === t.type).slice(0, 3);
    mine.forEach((h, j) => {
      const deg = t.deg + (j - (mine.length - 1) / 2) * 9;
      hubPts.push({ ...h, deg, p: pt(deg, 462, 276), from: t.p, typeColor: TYPE_COLORS[t.type] || TYPE_COLORS.other });
    });
  }
  return { lobePts, typePts, hubPts };
}

function Verdict({ k, v }) {
  const level = v?.level || (v?.ok ? 'ok' : 'warn');
  return (
    <span className={`bh-verdict ${LEVEL_CLASS[level] || 'is-warn'}`} title={v?.reason || ''}>
      <i aria-hidden="true" />{k}
    </span>
  );
}

export default function BrainHero({ name, sub, pages, verdicts = {}, onOpenCenter, lobes = [], wiki = { types: [], hubs: [] }, heat = null, note = '' }) {
  const narrow = useNarrow();
  const [hover, setHover] = useState(null); // { kind: 'lobe'|'type'|'hub', id }
  const types = useMemo(() => (wiki.types || []).filter((t) => TYPE_ORDER.includes(t.type)).sort((a, b) => TYPE_ORDER.indexOf(a.type) - TYPE_ORDER.indexOf(b.type)), [wiki.types]);
  const hubs = useMemo(() => wiki.hubs || [], [wiki.hubs]);
  const { lobePts, typePts, hubPts } = useMemo(() => layout(lobes, types, hubs), [lobes, types, hubs]);
  const heatOf = (h) => (heat ? heat[h.key] || heat[h.slug] || heat[h.docId] || 0 : 0);
  const verdictList = ['populated', 'updating', 'current'].filter((k) => verdicts[k]);

  const nova = [
    `Brain overview for ${name || 'this project'}: ${fmt(pages)} wiki pages.`,
    verdictList.length ? `Verdicts: ${verdictList.map((k) => `${k} ${verdicts[k].level || (verdicts[k].ok ? 'ok' : 'warn')}`).join(', ')}.` : '',
    lobes.length ? `Lobes: ${lobes.map((l) => `${l.label} ${fmt(l.value)}`).join('; ')}.` : '',
    types.length ? `Wiki by type: ${types.map((t) => `${t.count} ${TYPE_LABEL[t.type] || t.type}`).join(', ')}; the satellites are the most-linked pages of each type.` : '',
    note,
  ].filter(Boolean).join(' ');

  if (narrow) {
    return (
      <section className="bh bh-list" data-nova-note={nova}>
        <div className="bh-list-head">
          <button type="button" className="bh-list-nucleus" onClick={onOpenCenter} title="Open the map">
            <strong>{name || 'The brain'}</strong>
            <span className="bh-nucleus-n">{fmt(pages)}</span>
            <span className="bh-nucleus-l">wiki pages</span>
          </button>
          <div className="bh-verdicts">{verdictList.map((k) => <Verdict key={k} k={k} v={verdicts[k]} />)}</div>
        </div>
        <ul className="bh-list-lobes">
          {lobes.map((l) => (
            <li key={l.id}>
              <button type="button" className={`bh-lobe ${l.tone ? `is-${l.tone}` : ''}`} onClick={l.onOpen} disabled={!l.onOpen}>
                <span className="bh-lobe-val">{fmt(l.value)}{l.live ? <i className="bh-live" aria-label="active in the last hour" /> : null}</span>
                <span className="bh-lobe-label">{l.label}</span>
                {l.sub ? <span className="bh-lobe-sub">{l.sub}</span> : null}
              </button>
            </li>
          ))}
        </ul>
        {types.length ? (
          <div className="bh-list-types">
            {types.map((t) => (
              <button key={t.type} type="button" className="bh-type" style={{ '--c': TYPE_COLORS[t.type] }} onClick={t.onOpen} disabled={!t.onOpen}>
                <i aria-hidden="true" /><span className="bh-type-n">{fmt(t.count)}</span><span className="bh-type-l">{TYPE_LABEL[t.type] || t.type}</span>
              </button>
            ))}
          </div>
        ) : null}
      </section>
    );
  }

  const C = { x: CX, y: CY };
  const hovered = (kind, id) => hover && hover.kind === kind && hover.id === id;
  const hubLit = (h) => hovered('hub', h.key) || hovered('type', h.type);
  const hoverHub = hover?.kind === 'hub' ? hubPts.find((h) => h.key === hover.id) : null;

  return (
    <section className="bh bh-radial" data-nova-note={nova}>
      <span className="bh-caption is-left">How it runs</span>
      <span className="bh-caption is-right">What it knows</span>
      <svg className="bh-svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true">
        {lobePts.map((l, i) => (
          <path key={l.id} d={dendrite(pt(lobePts.length === 1 ? 180 : 125 + (i * 110) / (lobePts.length - 1), 116, 116), l.p)} pathLength="1" className={`bh-dendrite ${hovered('lobe', l.id) ? 'is-on' : ''} ${hover && !hovered('lobe', l.id) ? 'is-dim' : ''}`} style={{ '--i': i }} />
        ))}
        {typePts.map((t, i) => (
          <path key={t.type} d={dendrite(pt(t.deg, 116, 116), t.p)} pathLength="1" className={`bh-dendrite is-type ${hovered('type', t.type) ? 'is-on' : ''} ${hover && !hovered('type', t.type) && !(hover.kind === 'hub' && hoverHub?.type === t.type) ? 'is-dim' : ''}`} style={{ '--i': i + lobePts.length, '--c': TYPE_COLORS[t.type] }} />
        ))}
        {hubPts.map((h, i) => (
          <path key={h.key} d={dendrite(h.from, h.p)} pathLength="1" className={`bh-fibre ${hubLit(h) ? 'is-on' : ''}`} style={{ '--i': i + lobePts.length + typePts.length, '--c': h.typeColor }} />
        ))}
        {verdictList.map((k, i) => {
          const v = verdicts[k];
          const level = v?.level || (v?.ok ? 'ok' : 'warn');
          return <path key={k} d={arcPath(84 + i * 11)} pathLength="1" className={`bh-arc ${LEVEL_CLASS[level] || 'is-warn'}`} style={{ '--i': i }}><title>{`${k}: ${v?.reason || level}`}</title></path>;
        })}
        <circle cx={C.x} cy={C.y} r={64} className="bh-core" />
      </svg>

      <button type="button" className="bh-nucleus" style={at(C)} onClick={onOpenCenter} title="Open the map">
        <strong className="bh-nucleus-name">{name || 'The brain'}</strong>
        <span className="bh-nucleus-n">{fmt(pages)}</span>
        <span className="bh-nucleus-l">wiki pages</span>
        <span className="bh-nucleus-v">{verdictList.map((k) => <i key={k} className={`${LEVEL_CLASS[verdicts[k].level || (verdicts[k].ok ? 'ok' : 'warn')]}`} title={`${k}: ${verdicts[k].reason || ''}`} />)}</span>
      </button>
      {sub ? <span className="bh-sub" style={at({ x: CX, y: CY + 128 })}>{sub}</span> : null}
      <div className="bh-verdicts bh-verdicts-float" style={at({ x: CX, y: CY - 128 })}>{verdictList.map((k) => <Verdict key={k} k={k} v={verdicts[k]} />)}</div>

      {lobePts.map((l) => (
        <button
          key={l.id} type="button"
          className={`bh-lobe ${l.tone ? `is-${l.tone}` : ''} ${hovered('lobe', l.id) ? 'is-hover' : ''}`}
          style={at(l.p)} onClick={l.onOpen} disabled={!l.onOpen}
          onMouseEnter={() => setHover({ kind: 'lobe', id: l.id })} onMouseLeave={() => setHover(null)}
          onFocus={() => setHover({ kind: 'lobe', id: l.id })} onBlur={() => setHover(null)}
          title={l.title || l.sub || l.label}
        >
          <span className="bh-lobe-val">{fmt(l.value)}{l.live ? <i className="bh-live" aria-label="active in the last hour" /> : null}</span>
          <span className="bh-lobe-label">{l.label}</span>
          {l.sub ? <span className="bh-lobe-sub">{l.sub}</span> : null}
        </button>
      ))}

      {typePts.map((t) => (
        <button
          key={t.type} type="button"
          className={`bh-type ${hovered('type', t.type) ? 'is-hover' : ''}`}
          style={{ ...at(t.p), '--c': TYPE_COLORS[t.type] }} onClick={t.onOpen} disabled={!t.onOpen}
          onMouseEnter={() => setHover({ kind: 'type', id: t.type })} onMouseLeave={() => setHover(null)}
          onFocus={() => setHover({ kind: 'type', id: t.type })} onBlur={() => setHover(null)}
          title={`${fmt(t.count)} ${TYPE_LABEL[t.type] || t.type} — open the list`}
        >
          <i aria-hidden="true" /><span className="bh-type-n">{fmt(t.count)}</span><span className="bh-type-l">{TYPE_LABEL[t.type] || t.type}</span>
        </button>
      ))}

      {hubPts.map((h) => {
        const served = heatOf(h);
        return (
          <button
            key={h.key} type="button"
            className={`bh-hub ${h.stale || h.thin ? 'is-faded' : ''} ${h.errors ? 'is-error' : ''} ${served ? 'is-hot' : ''} ${hubLit(h) ? 'is-hover' : ''}`}
            style={{ ...at(h.p), '--c': h.typeColor }} onClick={h.onOpen}
            onMouseEnter={() => setHover({ kind: 'hub', id: h.key })} onMouseLeave={() => setHover(null)}
            onFocus={() => setHover({ kind: 'hub', id: h.key })} onBlur={() => setHover(null)}
            aria-label={`${h.title} — ${h.backlinks} inbound links${served ? `, served ${served} times lately` : ''}`}
          />
        );
      })}

      {hoverHub ? (
        <div className={`bh-tip ${hoverHub.p.x > CX + 300 ? 'is-left' : ''}`} style={at({ x: hoverHub.p.x, y: hoverHub.p.y + 16 })} role="status">
          <strong>{hoverHub.title}</strong>
          <span>{TYPE_LABEL[hoverHub.type] || hoverHub.type} · {hoverHub.backlinks} inbound{heatOf(hoverHub) ? ` · served ${heatOf(hoverHub)}×` : ''}{hoverHub.stale ? ' · stale' : ''}{hoverHub.thin ? ' · thin' : ''}{hoverHub.errors ? ' · lint error' : ''}</span>
        </div>
      ) : null}
    </section>
  );
}
